/*
 * gro-analytics.js: on-site merchandising events for GA4 (GROGROCERY)
 *
 *   view_promotion    a banner slot was at least 50% on screen for 1 s (once per slot per page view;
 *                     header bars once per session)
 *   select_promotion  a banner link was clicked
 *   select_item       a product card was clicked in a collection/search grid or a product carousel;
 *                     a collection-grid click also carries the grid's view (below) and card_photo
 *   view_cart         the cart drawer opened (the Google app only sends view_cart on the /cart page,
 *                     which almost nobody visits, so the drawer step is otherwise invisible)
 *   gro_grid_view     a collection grid was shown: once per page view, and again when the shopper
 *                     switches Product / Model. grid_view, view_source, grid_size, cards_with_model
 *   gro_grid_switch   the shopper changed the grid's photos or size (control: photo | grid_size)
 *
 * Default-view split test: a shopper the collection grid has assigned an arm carries it as the
 * user property grid_test_arm (product | model) on every event from this browser, including the
 * Google app's own add_to_cart and purchase; grid events also carry it as a parameter on the
 * collections in the test.
 *
 * Transport: the Google & YouTube app runs gtag.js in the main window (web pixel, runtimeContext
 * OPEN), so these events join its _ga client and session. Everything goes to G-T3BHN8ZYXM only,
 * never to the Merchant Center tag.
 *
 * Placements are found by the theme's own markup (PLACEMENTS below). Any other element can opt in
 * with data-gro-promo plus optional data-promo-name / data-promo-id / data-promo-creative /
 * data-promo-slot attributes.
 *
 * Debug: add ?gro_debug=1 to any URL (sticks for the tab; ?gro_debug=0 clears it). Every event is
 * logged to the console and flagged debug_mode so it shows in GA4 DebugView. ?gro_debug=dry logs
 * the same way but sends nothing, for QA that must not reach GA4.
 */
(function () {
  'use strict';

  var GA_ID = 'G-T3BHN8ZYXM';
  var VIEW_RATIO = 0.5; // share of the slot (or of the viewport, for slots taller than it)
  var VIEW_MS = 1000; // continuous time on screen before a view counts
  var GRID_PAGE_SIZE = 36; // products per collection page (main-collection-product-grid setting)

  if (window.__groAnalytics) return;
  window.__groAnalytics = true;

  /* ---------- debug ---------- */
  var DEBUG = false;
  var DRY = false;
  try {
    var debugArg = location.search.match(/[?&]gro_debug=(0|1|dry)(?:&|$)/);
    if (debugArg && debugArg[1] === '0') sessionStorage.removeItem('gro_debug');
    else if (debugArg) sessionStorage.setItem('gro_debug', debugArg[1]);
    DRY = sessionStorage.getItem('gro_debug') === 'dry';
    DEBUG = DRY || sessionStorage.getItem('gro_debug') === '1';
  } catch (e) {}

  /* ---------- transport ---------- */
  var queue = [];
  function gtagCall(args) {
    if (DRY) return;
    if (typeof window.gtag === 'function') window.gtag.apply(window, args);
    else queue.push(args);
  }
  function send(name, params) {
    params.send_to = GA_ID;
    if (DEBUG) {
      if (!DRY) params.debug_mode = true;
      console.log('[gro-analytics]' + (DRY ? ' dry run, not sent:' : ''), name, JSON.parse(JSON.stringify(params)));
    }
    gtagCall(['event', name, params]);
  }
  // The Google app loads gtag asynchronously. Flush anything queued before it arrived, in order;
  // give up after ~20 s (tag blocked or consent refused), since those calls would never be sent.
  var flushTries = 0;
  var flushTimer = setInterval(function () {
    if (typeof window.gtag === 'function') {
      clearInterval(flushTimer);
      queue.splice(0).forEach(function (args) { window.gtag.apply(window, args); });
    } else if (++flushTries > 40) {
      clearInterval(flushTimer);
      queue.length = 0;
    }
  }, 500);

  /* ---------- default-view split test ---------- */
  // The collection grid's inline script assigns each new shopper a sticky arm (localStorage
  // gro:grid-test) while the test runs. Set before any event goes out, the user property labels
  // everything this browser sends, the Google app's add_to_cart and purchase included, so the
  // test reads through to the sale.
  var GRID_TEST = 'grid_default_1'; // must match the id in main-collection-product-grid.liquid
  var testArm = null;
  try {
    var testRec = JSON.parse(localStorage.getItem('gro:grid-test'));
    if (testRec && testRec.id === GRID_TEST && (testRec.arm === 'product' || testRec.arm === 'model')) testArm = testRec.arm;
  } catch (e) {}
  if (testArm) {
    if (DEBUG) console.log('[gro-analytics]' + (DRY ? ' dry run, not sent:' : ''), 'user_properties', { grid_test_arm: testArm });
    gtagCall(['set', 'user_properties', { grid_test_arm: testArm }]);
  }

  /* ---------- page context ---------- */
  var meta = (window.ShopifyAnalytics && window.ShopifyAnalytics.meta) || {};
  var page = meta.page || {};
  var pageType = page.pageType || 'other'; // home, collection, product, page, searchresults, cart ...
  var LOCALE_PREFIX = /^\/(zh|zh-tw|zh-hant|en|en-[a-z]{2})(?=\/|$)/i;
  var path = location.pathname.replace(LOCALE_PREFIX, '') || '/';
  var handle = (path.match(/^\/(?:collections|products|pages|blogs)\/([^/?#]+)/) || [])[1] || '';
  var LOCATION = pageType === 'home' ? 'home' : pageType + (handle ? ':' + decodeURIComponent(handle) : '');
  var CURRENCY = (window.Shopify && Shopify.currency && Shopify.currency.active) || 'HKD';

  function text(el) {
    if (!el) return '';
    // Skip screen-reader-only labels (Dawn prefixes collection titles with a hidden "Collection:").
    var copy = el.cloneNode(true);
    copy.querySelectorAll('.visually-hidden').forEach(function (n) { n.remove(); });
    return copy.textContent.replace(/\s+/g, ' ').trim().slice(0, 100);
  }
  function linkPath(a) {
    var href = a && a.getAttribute('href');
    if (!href) return '';
    try {
      var u = new URL(href, location.href);
      return u.hostname === location.hostname ? u.pathname.replace(LOCALE_PREFIX, '') || '/' : u.hostname + u.pathname;
    } catch (e) {
      return href;
    }
  }

  /* ---------- promotion placements ---------- */
  // root: container selector; slots: slot selectors inside root, tried in order until one matches
  // (':scope' = the root itself); link: the clickable link inside a slot; name: what is promoted.
  var PLACEMENTS = [
    { type: 'band', root: '.full-card-carousel__container', slots: ['swiper-slide'], link: 'a[href]', name: 'h3' },
    { type: 'hero', root: 'slideshow-component', slots: ['.slideshow__slide'], link: '.banner__buttons a[href]', name: '.banner__heading' },
    { type: 'cta_grid', root: '.image-cta-grid__container', slots: ['.grid__item'], link: '.banner__buttons a[href]', name: 'h2' },
    { type: 'gwp_bar', root: '.gro-gwp', slots: [':scope'], link: '.gro-gwp__link', name: '.gro-gwp__text-full', session: true },
    { type: 'announcement_bar', root: '.announcement-bar-section', slots: ['.announcement-bar__announcement', '.announcement-bar'], link: '.announcement-bar__link', name: '.announcement-bar__message', session: true }
  ];
  function creativeFor(type, n) {
    // Header bars are the same placement everywhere; page-body placements are prefixed with the
    // page type so a homepage carousel and a collection band never share a name.
    var base = /_bar$/.test(type) ? type : (pageType === 'home' ? 'home' : pageType) + '_' + type;
    return n > 0 ? base + '_' + (n + 1) : base;
  }

  var slotsByEl = new Map(); // slot element -> record
  var slotsByLink = new Map(); // link element -> record

  function addRecord(el, rec) {
    if (slotsByEl.has(el)) return;
    slotsByEl.set(el, rec);
    if (rec.link) slotsByLink.set(rec.link, rec);
  }

  PLACEMENTS.forEach(function (p) {
    var seenRoots = [];
    document.querySelectorAll(p.root).forEach(function (root) {
      if (seenRoots.some(function (r) { return r.contains(root); })) return; // nested match
      seenRoots.push(root);
      var creative = creativeFor(p.type, seenRoots.length - 1);
      var slots = [];
      p.slots.some(function (sel) {
        slots = sel === ':scope' ? [root] : Array.prototype.slice.call(root.querySelectorAll(sel));
        return slots.length > 0;
      });
      slots.forEach(function (slot, i) {
        var link = slot.matches && slot.matches('a[href]') ? slot : slot.querySelector(p.link);
        var name = text(slot.querySelector(p.name)) || text(link);
        addRecord(slot, {
          el: slot,
          link: link,
          promotion_id: link ? linkPath(link) : creative + ':' + (i + 1),
          promotion_name: name,
          creative_name: creative,
          creative_slot: String(i + 1),
          session: !!p.session
        });
      });
    });
  });

  // Explicit opt-in for anything the table above does not know about.
  document.querySelectorAll('[data-gro-promo]').forEach(function (el, i) {
    var link = el.matches('a[href]') ? el : el.querySelector('a[href]');
    addRecord(el, {
      el: el,
      link: link,
      promotion_id: el.getAttribute('data-promo-id') || (link ? linkPath(link) : 'promo:' + (i + 1)),
      promotion_name: el.getAttribute('data-promo-name') || text(el.querySelector('h1,h2,h3')) || text(link),
      creative_name: el.getAttribute('data-promo-creative') || creativeFor('custom', 0),
      creative_slot: el.getAttribute('data-promo-slot') || String(i + 1),
      session: el.hasAttribute('data-promo-once-per-session')
    });
  });

  function promoParams(rec) {
    var promo = {
      promotion_id: rec.promotion_id,
      promotion_name: rec.promotion_name,
      creative_name: rec.creative_name,
      creative_slot: rec.creative_slot,
      location_id: LOCATION
    };
    var params = Object.assign({}, promo);
    // Promotion fields also go on one item so GA4's built-in "Item promotion ..." dimensions and
    // the items viewed/clicked in promotion metrics fill in. The item is a labelled stand-in for
    // the destination, not a product.
    params.items = [Object.assign({ item_id: 'promo:' + rec.promotion_id, item_name: 'Promo: ' + (rec.promotion_name || rec.promotion_id) }, promo)];
    return params;
  }

  /* ---------- impressions ---------- */
  function sessionSeen(rec) {
    var key = 'gro_pv:' + rec.creative_name + ':' + rec.promotion_id + ':' + rec.promotion_name;
    try {
      if (sessionStorage.getItem(key)) return true;
      sessionStorage.setItem(key, '1');
    } catch (e) {}
    return false;
  }
  function fireView(rec) {
    rec.viewed = true;
    if (io) io.unobserve(rec.el);
    if (rec.session && sessionSeen(rec)) return;
    send('view_promotion', promoParams(rec));
  }
  var io = null;
  if ('IntersectionObserver' in window && slotsByEl.size) {
    io = new IntersectionObserver(function (entries) {
      entries.forEach(function (en) {
        var rec = slotsByEl.get(en.target);
        if (!rec || rec.viewed) return;
        var r = en.intersectionRect;
        var onScreen = en.isIntersecting && (en.intersectionRatio >= VIEW_RATIO ||
          r.width * r.height >= VIEW_RATIO * window.innerWidth * window.innerHeight);
        if (onScreen && !rec.timer) {
          rec.timer = setTimeout(function () {
            rec.timer = null;
            if (document.visibilityState === 'visible') fireView(rec);
          }, VIEW_MS);
        } else if (!onScreen && rec.timer) {
          clearTimeout(rec.timer);
          rec.timer = null;
        }
      });
    }, { threshold: [0, 0.25, 0.5, 0.75, 1] });
    slotsByEl.forEach(function (rec) { io.observe(rec.el); });
  }

  /* ---------- product lists ---------- */
  function collectionList() {
    return {
      id: page.resourceId ? String(page.resourceId) : handle,
      name: text(document.querySelector('.collection-hero__title, main h1')) || handle
    };
  }
  function listContext(card) {
    var grid = card.closest('#product-grid');
    if (grid) {
      var items = Array.prototype.slice.call(grid.children);
      var pos = items.indexOf(card.closest('#product-grid > *'));
      var pageNum = parseInt(new URLSearchParams(location.search).get('page') || '1', 10) || 1;
      if (pageType === 'collection') {
        var coll = collectionList();
        return { id: coll.id, name: coll.name, index: pos + (pageNum - 1) * GRID_PAGE_SIZE };
      }
      return { id: 'search', name: 'Search results', index: pos + (pageNum - 1) * GRID_PAGE_SIZE };
    }
    var section = card.closest('.shopify-section');
    var inHeader = !!card.closest('header, .header-wrapper, .mega-menu, .menu-drawer');
    var name;
    if (inHeader) {
      var menu = card.closest('details');
      name = 'Menu: ' + (text(menu && menu.querySelector('summary')) || 'products');
    } else {
      name = text(section && section.querySelector('h2')) || 'Product carousel';
    }
    var container = card.closest('swiper-container, .slider, ul, .grid') || section;
    var listItems = container ? Array.prototype.slice.call(container.querySelectorAll('.card-wrapper')) : [];
    return {
      id: section && section.id ? section.id.replace(/^shopify-section-(template--\d+__|sections--\d+__)?/, '') : 'list',
      name: name,
      index: Math.max(0, listItems.indexOf(card))
    };
  }
  function price(card) {
    var el = card.querySelector('.price-item--sale, .price-item--last, .price-item--regular');
    var n = el ? parseFloat(el.textContent.replace(/[^0-9.]/g, '')) : NaN;
    return isNaN(n) ? undefined : n;
  }

  /* ---------- collection grid view: product / model photos, grid size ---------- */
  // The grid's wrapper (.gro-view) holds the view as attributes: data-gro-photo (product | model),
  // data-gro-density (large | standard | compact) and data-gro-view-source (default = the
  // collection's or template's default; test = the default-view test's arm; saved = the
  // shopper's pick from an earlier page; switched = changed on this page). Each grid view and each grid click carries them, so click-through
  // reads per view: select_item ÷ gro_grid_view, split by grid_view.
  var gridRoot = pageType === 'collection' ? document.querySelector('.gro-view') : null;
  function gridState() {
    var state = {
      grid_view: gridRoot.getAttribute('data-gro-photo') || 'product',
      view_source: gridRoot.getAttribute('data-gro-view-source') || 'default',
      grid_size: gridRoot.getAttribute('data-gro-density') || 'standard'
    };
    // Only on collections in the default-view test (not GROCERY Women), so grid events here read
    // the test cleanly: assigned arm, whatever the shopper went on to pick.
    var arm = gridRoot.getAttribute('data-gro-test-arm');
    if (arm) state.grid_test_arm = arm;
    return state;
  }
  function sendGridView() {
    var grid = document.getElementById('product-grid');
    if (!grid) return;
    var coll = collectionList();
    send('gro_grid_view', Object.assign({
      item_list_id: coll.id,
      item_list_name: coll.name,
      cards: grid.querySelectorAll('.card-wrapper').length,
      cards_with_model: grid.querySelectorAll('.gro-photos').length
    }, gridState()));
  }
  if (gridRoot) {
    sendGridView();
    // gro-grid-view.js announces a change once it has applied it, so the state read here is the
    // new one. A photo switch is a new grid view; a size change is not.
    gridRoot.addEventListener('gro:view-change', function (ev) {
      var control = (ev.detail && ev.detail.control) || 'photo';
      send('gro_grid_switch', Object.assign({ item_list_name: collectionList().name, control: control }, gridState()));
      if (control === 'photo') sendGridView();
    });
  }

  /* ---------- clicks ---------- */
  document.addEventListener('click', function (ev) {
    var a = ev.target && ev.target.closest ? ev.target.closest('a[href]') : null;
    if (!a) return;

    var rec = slotsByLink.get(a);
    if (!rec) {
      var slotEl = a.closest('[data-gro-promo], swiper-slide, .slideshow__slide, .gro-gwp, .announcement-bar');
      rec = slotEl ? slotsByEl.get(slotEl) : null;
      if (rec && rec.link && rec.link !== a && !rec.link.contains(a)) rec = null;
    }
    if (rec) {
      var p = promoParams(rec);
      p.transport_type = 'beacon';
      send('select_promotion', p);
      return;
    }

    var card = a.closest('.card-wrapper');
    if (!card || a.closest('quick-add-modal, .quick-add')) return;
    var cardLink = card.querySelector('a[id^="CardLink-"]');
    var pid = cardLink ? cardLink.id.split('-').pop() : '';
    var list = listContext(card);
    var item = {
      item_id: pid ? 'shopify_HK_' + pid : linkPath(a),
      item_name: text(card.querySelector('.card__heading')) || text(a),
      index: list.index,
      item_list_id: list.id,
      item_list_name: list.name,
      price: price(card)
    };
    var params = {
      item_list_id: list.id,
      item_list_name: list.name,
      currency: CURRENCY,
      transport_type: 'beacon',
      items: [item]
    };
    // A collection-grid card also carries the grid's view, and the photo this card led with:
    // model or product, or product_only when the product has no model shot to lead with.
    if (gridRoot && gridRoot.contains(card) && card.closest('#product-grid')) {
      var view = gridState();
      Object.assign(params, view, { card_photo: card.querySelector('.gro-photos') ? view.grid_view : 'product_only' });
    }
    send('select_item', params);
  }, true);

  /* ---------- cart drawer ---------- */
  // Dawn's <cart-drawer> gains the "active" class when it opens (after add-to-cart or the cart
  // icon). One view_cart per opening, with the cart's contents at that moment.
  var drawer = document.querySelector('cart-drawer');
  if (drawer && 'MutationObserver' in window) {
    var drawerOpen = drawer.classList.contains('active');
    new MutationObserver(function () {
      var open = drawer.classList.contains('active');
      if (open && !drawerOpen) {
        var root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
        fetch(root + 'cart.js', { credentials: 'same-origin' })
          .then(function (r) { return r.json(); })
          .then(function (cart) {
            if (!cart.item_count) return; // an empty bag opened from the header is not a cart step
            send('view_cart', {
              currency: cart.currency || CURRENCY,
              value: cart.total_price / 100,
              cart_location: 'drawer',
              items: (cart.items || []).slice(0, 50).map(function (it, i) {
                return {
                  item_id: 'shopify_HK_' + it.product_id + '_' + it.variant_id,
                  item_name: it.product_title,
                  item_variant: it.variant_title || undefined,
                  price: it.final_price / 100,
                  quantity: it.quantity,
                  index: i
                };
              })
            });
          })
          .catch(function () {});
      }
      drawerOpen = open;
    }).observe(drawer, { attributes: true, attributeFilter: ['class'] });
  }

  if (DEBUG) {
    console.log('[gro-analytics] ready', {
      location: LOCATION,
      promoSlots: Array.from(slotsByEl.values()).map(function (r) {
        return r.creative_name + ' #' + r.creative_slot + ' ' + r.promotion_name + ' -> ' + r.promotion_id;
      })
    });
  }
})();
